import { motion } from 'framer-motion';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { PageTransition } from '../../components/Transitions/PageTransition';
import { Spinner } from '../../components/Feedback/Spinner';
import { safeRedirect } from '../../lib/safeRedirect';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithPassword } = useAuth();
  const { t } = useTranslation();

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const from = safeRedirect((location.state as { from?: unknown } | null)?.from);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await loginWithPassword(phone, password);
      if (response.is_new_user || !response.user.onboarding_completed) {
        navigate('/onboarding');
      } else {
        navigate(from);
      }
      toast.success(t('auth.signedIn'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.invalidCredentials');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <svg className="w-14 h-14 mx-auto" viewBox="0 0 100 100" fill="none" aria-hidden="true">
              <defs>
                <linearGradient id="ra2" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#0d7377"/>
                  <stop offset="100%" stopColor="#095f62"/>
                </linearGradient>
              </defs>
              <rect width="100" height="100" rx="22" fill="url(#ra2)"/>
              <path d="M28 72 Q28 44 50 32 Q72 44 72 72" fill="none" stroke="white" strokeWidth="5.5" strokeLinecap="round"/>
              <path d="M36 72 Q36 52 50 44 Q64 52 64 72" fill="none" stroke="white" strokeWidth="5.5" strokeLinecap="round" opacity="0.35"/>
              <line x1="50" y1="20" x2="50" y2="32" stroke="white" strokeWidth="5.5" strokeLinecap="round"/>
              <circle cx="50" cy="18" r="4" fill="white"/>
            </svg>
            <h1 className="mt-4 text-3xl font-bold text-text-primary">{t('auth.welcomeTitle')}</h1>
            <p className="mt-2 text-text-muted">{t('auth.welcomeSubtitle')}</p>
          </div>

          <div className="bg-surface rounded-xl shadow-sm border border-border p-8">
            <form onSubmit={handlePasswordLogin}>
              <div className="mb-4">
                <label htmlFor="phone-login" className="block text-sm font-medium text-text-secondary mb-2">
                  {t('auth.phoneLabel')}
                </label>
                <input
                  id="phone-login"
                  type="tel"
                  autoComplete="username"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('auth.phonePlaceholder')}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                  required
                />
              </div>
              <div className="mb-6">
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <label htmlFor="password" className="block text-sm font-medium text-text-secondary">
                    {t('auth.password')}
                  </label>
                  <Link to="/forgot-password" className="text-xs text-brand-primary hover:underline">
                    {t('auth.reset.forgot')}
                  </Link>
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                  required
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-status-danger/10 text-status-danger text-sm rounded-lg">
                  {error}
                </div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="w-full py-3 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-secondary disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner className="h-4 w-4 border-white border-t-transparent" />
                    {t('auth.signingIn')}
                  </span>
                ) : (
                  t('auth.signIn')
                )}
              </motion.button>
            </form>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <p className="text-center text-sm text-text-muted">
              {t('auth.noAccount')}{' '}
              <Link to="/signup" className="text-brand-primary font-medium hover:underline">
                {t('auth.signUp')}
              </Link>
            </p>
            <p className="text-center text-sm text-text-muted">
              <Trans
                i18nKey="auth.terms"
                components={{
                  terms: <Link to="/terms" className="underline underline-offset-2 hover:text-text-primary" />,
                  privacy: <Link to="/privacy" className="underline underline-offset-2 hover:text-text-primary" />,
                }}
              />
            </p>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
